import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, money, catLabel, asset, timeLeft } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gavel, Clock, Layers, Package, ShieldCheck, Trophy, Lock, CheckCircle2, Store, Truck, Camera, ImagePlus } from "lucide-react";

export default function AuctionDetail() {
  const { auctionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [active, setActive] = useState(0);
  const [bid, setBid] = useState("");
  const [bidding, setBidding] = useState(false);
  const [t, setT] = useState({ label: "", ended: false });
  const [shipForm, setShipForm] = useState({ carrier: "", tracking_number: "", note: "" });
  const [savingShip, setSavingShip] = useState(false);
  const [addingPhotos, setAddingPhotos] = useState(false);
  const addFileRef = useRef(null);
  const addCamRef = useRef(null);

  const onAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAddingPhotos(true);
    try {
      const urls = [];
      for (const file of files.slice(0, 12)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/auctions/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      await api.post(`/auctions/${auctionId}/images`, { images: urls });
      toast.success("Photos added to your listing");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add photos");
    } finally {
      setAddingPhotos(false);
      if (addFileRef.current) addFileRef.current.value = "";
      if (addCamRef.current) addCamRef.current.value = "";
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/auctions/${auctionId}`);
      setA(data);
      setBid((prev) => prev || String(data.min_next_bid));
    } catch {
      setA(false);
    }
  }, [auctionId]);

  useEffect(() => { load(); }, [load]);

  // Live countdown + gentle polling so bids/price stay fresh.
  useEffect(() => {
    if (!a) return;
    setT(timeLeft(a.ends_at));
    const tick = setInterval(() => setT(timeLeft(a.ends_at)), 1000);
    const poll = setInterval(load, 5000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [a, load]);

  if (a === false) return <Shell><div className="text-center py-24 text-slate-500">Auction not found.</div></Shell>;
  if (!a) return <Shell><div className="text-center py-24 text-slate-500">Loading…</div></Shell>;

  const isEstate = a.listing_type === "estate";
  const isSeller = user && user.user_id === a.seller_id;
  const isLive = a.status === "live" && !t.ended;
  const youWon = user && a.winner_id === user.user_id;
  const gallery = a.images && a.images.length ? a.images : (a.image_url ? [a.image_url] : []);

  const placeBid = async () => {
    if (!user) { navigate("/login"); return; }
    const amt = Number(bid);
    if (!amt || amt < a.min_next_bid) return toast.error(`Bid at least ${money(a.min_next_bid)}`);
    setBidding(true);
    try {
      const { data } = await api.post(`/auctions/${auctionId}/bid`, { amount: amt });
      toast.success(`You're the top bidder at ${money(data.current_price)}`);
      setBid(String(data.min_next_bid));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bid failed");
      load();
    } finally {
      setBidding(false);
    }
  };

  const payNow = async () => {
    try {
      const { data } = await api.post(`/auctions/${auctionId}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
    }
  };

  const saveShipping = async () => {
    if (!shipForm.carrier && !shipForm.tracking_number) return toast.error("Add a carrier or tracking number");
    setSavingShip(true);
    try {
      await api.post(`/auctions/${auctionId}/shipping`, shipForm);
      toast.success("Tracking sent to the buyer");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save tracking");
    } finally {
      setSavingShip(false);
    }
  };

  return (
    <Shell>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/3] sm:aspect-square rounded-xl overflow-hidden bg-[#121721] border border-white/10 flex items-center justify-center" data-testid="auction-main-image">
            {gallery.length ? (
              <img src={asset(gallery[active])} alt={a.title} className="w-full h-full object-cover" />
            ) : (
              isEstate ? <Layers className="h-16 w-16 text-slate-700" /> : <Package className="h-16 w-16 text-slate-700" />
            )}
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-6 gap-2 mt-3">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${active === i ? "border-amber-500" : "border-transparent opacity-60 hover:opacity-100"}`}>
                  <img src={asset(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {isSeller && (a.status === "live" || a.status === "scheduled") && (
            <div className="mt-3" data-testid="seller-add-photos">
              <input ref={addFileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={onAddPhotos} className="hidden" />
              <input ref={addCamRef} type="file" accept="image/*" capture="environment" onChange={onAddPhotos} className="hidden" />
              <div className="flex gap-2">
                <Button type="button" data-testid="detail-camera-btn" onClick={() => addCamRef.current?.click()} disabled={addingPhotos} variant="outline"
                  className="flex-1 rounded-full h-10 border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20">
                  <Camera className="h-4 w-4 mr-1.5" /> Take photo
                </Button>
                <Button type="button" data-testid="detail-upload-btn" onClick={() => addFileRef.current?.click()} disabled={addingPhotos} variant="outline"
                  className="flex-1 rounded-full h-10 border-white/15 bg-white/5 text-white hover:bg-white/10">
                  <ImagePlus className="h-4 w-4 mr-1.5" /> {addingPhotos ? "Adding…" : "Add photos"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">Add more angles any time while your auction is live.</p>
            </div>
          )}
        </div>

        {/* Info + bidding */}
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
              {isEstate && <Layers className="h-3 w-3" />} {catLabel(a.category)}
            </span>
            {isEstate && <span className="text-xs text-slate-500">Whole lot, sold together</span>}
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{a.title}</h1>

          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-400">
            {a.shop_name && a.shop_slug && (
              <Link to={`/shop/${a.shop_slug}`} data-testid="auction-shop-link" className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 font-medium border border-amber-500/30 rounded-full px-2.5 py-0.5">
                <Store className="h-3.5 w-3.5" /> {a.shop_name}
              </Link>
            )}
            <span>Seller: <span className="text-slate-200">{a.seller_name}</span></span>
            {a.seller_trust?.verified_lister && (
              <span className="inline-flex items-center gap-1 text-xs text-green-400"><ShieldCheck className="h-3.5 w-3.5" /> Verified seller</span>
            )}
            <span className="text-slate-600">· trust {a.seller_trust?.score}/100</span>
          </div>

          {/* Price / timer box */}
          <div className="rounded-xl bg-[#121721] border border-white/10 p-5 space-y-4">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{a.bid_count > 0 ? "Current bid" : "Starting bid"}</div>
                <div className="text-3xl font-mono font-bold text-amber-400" data-testid="auction-current-price">{money(a.current_price)}</div>
                <div className="text-xs text-slate-500 mt-1">{a.bid_count} bid{a.bid_count === 1 ? "" : "s"}{a.shipping_cost > 0 ? ` · +${money(a.shipping_cost)} shipping` : " · free shipping"}</div>
                {a.has_reserve && (
                  <div data-testid="reserve-status" className={`text-xs mt-1 font-medium ${a.reserve_met ? "text-green-400" : "text-amber-400"}`}>
                    {a.reserve_met ? "✓ Reserve met" : "Reserve not yet met"}
                  </div>
                )}
              </div>
              <div className={`text-right ${t.urgent && isLive ? "text-red-400" : "text-slate-200"}`}>
                <div className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1 justify-end"><Clock className="h-3 w-3" /> {isLive ? "Time left" : "Status"}</div>
                <div className="text-xl font-mono font-bold" data-testid="auction-time-left">
                  {a.status === "scheduled" ? "Upcoming" : isLive ? t.label : a.status}
                </div>
              </div>
            </div>

            {isLive && !isSeller && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input data-testid="bid-input" type="number" value={bid} onChange={(e) => setBid(e.target.value)}
                      className="pl-7 h-12 bg-[#0B0E14] border-white/10 text-white text-lg font-mono focus-visible:ring-amber-500" />
                  </div>
                  <Button data-testid="place-bid-btn" onClick={placeBid} disabled={bidding}
                    className="h-12 px-6 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg">
                    <Gavel className="h-4 w-4 mr-1.5" /> {user ? (bidding ? "Bidding…" : "Place bid") : "Sign in to bid"}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Enter {money(a.min_next_bid)} or more. Bids are binding. A bid in the final 2 minutes extends the auction.</p>
              </div>
            )}

            {isSeller && isLive && (
              <div className="text-sm text-slate-400 flex items-center gap-2"><Lock className="h-4 w-4" /> This is your listing — you can't bid on it.</div>
            )}

            {a.status === "sold" && (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-sm">
                <div className="flex items-center gap-2 text-amber-300 font-semibold"><Trophy className="h-4 w-4" /> Won by {a.winner_name} at {money(a.final_price)}</div>
                {youWon && a.payment_status !== "paid" && (
                  <Button data-testid="winner-pay-btn" onClick={payNow} className="mt-3 w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-11">
                    Pay {money((a.final_price || 0) + (a.shipping_cost || 0))} now{a.shipping_cost > 0 ? ` (incl. ${money(a.shipping_cost)} shipping)` : ""}
                  </Button>
                )}
                {isSeller && <div className="text-xs text-slate-400 mt-1">You'll receive {money(a.seller_proceeds)} after the 4% fee once the buyer pays.</div>}
              </div>
            )}
            {a.status === "paid" && (
              <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-400 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Paid — sold to {a.winner_name} for {money(a.final_price)}.
              </div>
            )}
            {a.status === "unsold" && (
              <div className="text-sm text-slate-400">This auction ended with no bids.</div>
            )}
          </div>

          {isSeller && isLive && a.relist_count > 0 && (
            <div className="text-xs text-sky-300 bg-sky-500/10 border border-sky-500/20 rounded-lg px-3 py-2" data-testid="relist-badge">
              Auto-relisted {a.relist_count}/10 — no bids yet. After 10 relists it comes down and you can relist it manually.
            </div>
          )}

          {(a.status === "sold" || a.status === "paid") && (isSeller || youWon) && (
            <div className="rounded-xl bg-[#121721] border border-white/10 p-5" data-testid="fulfillment-panel">
              <div className="flex items-center gap-2 text-white font-semibold mb-3"><Truck className="h-4 w-4 text-amber-400" /> Shipping &amp; tracking</div>
              {isSeller ? (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">Add the carrier and tracking number so {a.winner_name} knows it's on the way.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input data-testid="ship-carrier" placeholder="Carrier (USPS, UPS…)" value={shipForm.carrier || a.shipping?.carrier || ""} onChange={(e) => setShipForm((s) => ({ ...s, carrier: e.target.value }))} className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
                    <Input data-testid="ship-tracking" placeholder="Tracking number" value={shipForm.tracking_number || a.shipping?.tracking_number || ""} onChange={(e) => setShipForm((s) => ({ ...s, tracking_number: e.target.value }))} className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
                  </div>
                  <Input data-testid="ship-note" placeholder="Note for the buyer (optional)" value={shipForm.note || a.shipping?.note || ""} onChange={(e) => setShipForm((s) => ({ ...s, note: e.target.value }))} className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
                  <Button data-testid="ship-save-btn" onClick={saveShipping} disabled={savingShip} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-10">
                    {savingShip ? "Saving…" : a.shipped ? "Update tracking" : "Send tracking to buyer"}
                  </Button>
                  {a.shipped && <div className="text-xs text-green-400">Buyer can see this tracking now.</div>}
                </div>
              ) : a.shipped && a.shipping ? (
                <div className="text-sm text-slate-300 space-y-1" data-testid="buyer-tracking">
                  <div><span className="text-slate-500">Carrier:</span> {a.shipping.carrier || "—"}</div>
                  <div><span className="text-slate-500">Tracking:</span> <span className="font-mono">{a.shipping.tracking_number || "—"}</span></div>
                  {a.shipping.note && <div className="text-slate-400">"{a.shipping.note}"</div>}
                </div>
              ) : (
                <div className="text-sm text-slate-500">The seller hasn't added tracking yet — check back soon.</div>
              )}
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Description</h3>
            <p className="text-slate-300 whitespace-pre-line leading-relaxed">{a.description}</p>
            {a.item_details && <p className="text-sm text-slate-500 mt-3"><span className="text-slate-400">Details:</span> {a.item_details}</p>}
          </div>

          {/* Bid history */}
          <div className="rounded-xl bg-[#121721] border border-white/10 overflow-hidden" data-testid="bid-history">
            <div className="px-4 py-3 text-sm font-semibold text-white border-b border-white/10">Bid history</div>
            {(a.recent_bids || []).length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No bids yet — be the first.</div>
            ) : (
              a.recent_bids.map((b, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 last:border-0 text-sm">
                  <span className="text-slate-300">{b.bidder_name}</span>
                  <span className="font-mono text-amber-400 font-semibold">{money(b.amount)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0B0E14]">
    <Navbar />
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/marketplace" className="text-sm text-slate-500 hover:text-amber-400 mb-6 inline-block">← Back to auctions</Link>
      {children}
    </div>
  </div>
);
